/**
 * FormRenderer — multi-field structured form. Each field is rendered with
 * the right native input (text/number/date/time/select/radio/checkbox) so
 * the user gets the platform-appropriate keyboard/picker.
 *
 * Submit fires `onSendMessage(summary, {in_response_to, structured_value})`
 * where `structured_value` is `{[fieldName]: fieldValue}`. The summary text
 * (used as the user's transcript line) is a comma-joined `label: value`
 * string so the assistant has something readable in the conversation log
 * even when reading without metadata.
 *
 * History: form goes inert with each field showing its submitted value as
 * a key/value pair, no inputs.
 *
 * Validation here is best-effort — the agent treats `structured_value` as
 * authoritative anyway. We disable submit when required fields are empty
 * or out of min/max range; the server can still reject.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormContentItem, FormField, QuickReplyOption } from '@wg/types';
import type { Renderer } from '../registry';

type FieldValue = string | number | boolean | string[] | undefined;

function defaultFor(field: FormField): FieldValue {
  if (field.default_value !== undefined) {
    return field.default_value as FieldValue;
  }
  if (field.kind === 'multi_select' || field.kind === 'checkbox') return [];
  if (field.kind === 'number') return undefined;
  return '';
}

function summarize(
  fields: FormField[],
  values: Record<string, FieldValue>,
): string {
  return fields
    .map(f => {
      const v = values[f.name];
      if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) {
        return null;
      }
      const display = Array.isArray(v) ? v.join(', ') : String(v);
      return `${f.label}: ${display}`;
    })
    .filter((x): x is string => Boolean(x))
    .join(', ');
}

function isComplete(
  fields: FormField[],
  values: Record<string, FieldValue>,
): boolean {
  for (const f of fields) {
    if (!f.required) continue;
    const v = values[f.name];
    if (v === undefined || v === '' || v === null) return false;
    if (Array.isArray(v) && v.length === 0) return false;
    if (f.kind === 'number' && f.validation) {
      const num = Number(v);
      if (Number.isNaN(num)) return false;
      if (typeof f.validation.min === 'number' && num < f.validation.min)
        return false;
      if (typeof f.validation.max === 'number' && num > f.validation.max)
        return false;
    }
  }
  return true;
}

const FieldInput: React.FC<{
  field: FormField;
  value: FieldValue;
  onChange: (v: FieldValue) => void;
  inert: boolean;
}> = ({ field, value, onChange, inert }) => {
  const id = `wg-field-${field.name}`;

  if (inert) {
    const display =
      value === undefined || value === ''
        ? '—'
        : Array.isArray(value)
          ? value.join(', ')
          : String(value);
    return (
      <div className="wg-chat__form-row">
        <span className="wg-chat__form-label">{field.label}</span>
        <span className="wg-chat__form-static">{display}</span>
      </div>
    );
  }

  switch (field.kind) {
    case 'text':
    case 'number':
    case 'date':
    case 'time': {
      const inputType =
        field.kind === 'number'
          ? 'number'
          : field.kind === 'date'
            ? 'date'
            : field.kind === 'time'
              ? 'time'
              : 'text';
      const v = value as string | number | undefined;
      return (
        <div className="wg-chat__form-row">
          <label className="wg-chat__form-label" htmlFor={id}>
            {field.label}
            {field.required && <span aria-hidden> *</span>}
          </label>
          <input
            id={id}
            className="wg-chat__form-input"
            type={inputType}
            placeholder={field.placeholder}
            value={v === undefined ? '' : String(v)}
            min={field.validation?.min as number | undefined}
            max={field.validation?.max as number | undefined}
            step={field.validation?.step}
            pattern={field.validation?.pattern}
            required={field.required}
            onChange={e => {
              const raw = e.target.value;
              onChange(
                field.kind === 'number' && raw !== '' ? Number(raw) : raw,
              );
            }}
          />
          {field.help && (
            <span className="wg-chat__form-help">{field.help}</span>
          )}
        </div>
      );
    }

    case 'select': {
      const opts = (field.options || []) as QuickReplyOption[];
      return (
        <div className="wg-chat__form-row">
          <label className="wg-chat__form-label" htmlFor={id}>
            {field.label}
            {field.required && <span aria-hidden> *</span>}
          </label>
          <select
            id={id}
            className="wg-chat__form-input"
            value={(value as string | undefined) ?? ''}
            required={field.required}
            onChange={e => onChange(e.target.value)}
          >
            <option value="">{field.placeholder || 'Select…'}</option>
            {opts.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {field.help && (
            <span className="wg-chat__form-help">{field.help}</span>
          )}
        </div>
      );
    }

    case 'radio': {
      const opts = (field.options || []) as QuickReplyOption[];
      const v = (value as string | undefined) ?? '';
      return (
        <div
          className="wg-chat__form-row"
          role="radiogroup"
          aria-label={field.label}
        >
          <span className="wg-chat__form-label">
            {field.label}
            {field.required && <span aria-hidden> *</span>}
          </span>
          <div className="wg-chat__form-radio">
            {opts.map(o => (
              <label key={o.value} className="wg-chat__form-radio-item">
                <input
                  type="radio"
                  name={field.name}
                  value={o.value}
                  checked={v === o.value}
                  onChange={() => onChange(o.value)}
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
          {field.help && (
            <span className="wg-chat__form-help">{field.help}</span>
          )}
        </div>
      );
    }

    case 'multi_select':
    case 'checkbox': {
      const opts = (field.options || []) as QuickReplyOption[];
      const arr = (Array.isArray(value) ? value : []) as string[];
      const toggle = (val: string) => {
        if (arr.includes(val)) onChange(arr.filter(v => v !== val));
        else onChange([...arr, val]);
      };
      return (
        <div className="wg-chat__form-row">
          <span className="wg-chat__form-label">
            {field.label}
            {field.required && <span aria-hidden> *</span>}
          </span>
          <div className="wg-chat__form-checks">
            {opts.map(o => (
              <label key={o.value} className="wg-chat__form-check">
                <input
                  type="checkbox"
                  value={o.value}
                  checked={arr.includes(o.value)}
                  onChange={() => toggle(o.value)}
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
          {field.help && (
            <span className="wg-chat__form-help">{field.help}</span>
          )}
        </div>
      );
    }

    default:
      return null;
  }
};

const FormRenderer: Renderer = ({ item, ctx }) => {
  const { t } = useTranslation();
  const it = item as FormContentItem;
  const inert = ctx.isHistory;
  const initial = useMemo<Record<string, FieldValue>>(() => {
    if (inert && ctx.answeredWith && typeof ctx.answeredWith === 'object') {
      return { ...(ctx.answeredWith as Record<string, FieldValue>) };
    }
    return Object.fromEntries(it.fields.map(f => [f.name, defaultFor(f)]));
  }, [it.fields, inert, ctx.answeredWith]);

  const [values, setValues] = useState<Record<string, FieldValue>>(initial);
  const valid = !inert && isComplete(it.fields, values);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const summary = summarize(it.fields, values) || '(form submitted)';
    ctx.onSendMessage(summary, {
      in_response_to: it.prompt_id,
      structured_value: values as unknown,
    });
  };

  return (
    <form
      className="wg-chat__form"
      onSubmit={onSubmit}
      aria-label={it.title || t('form.default_aria_label')}
    >
      {it.title && <p className="wg-chat__form-title">{it.title}</p>}
      {it.fields.map(f => (
        <FieldInput
          key={f.name}
          field={f}
          value={values[f.name]}
          onChange={v => setValues(prev => ({ ...prev, [f.name]: v }))}
          inert={inert}
        />
      ))}
      {!inert && (
        <div className="wg-chat__form-actions">
          <button
            type="submit"
            className="wg-chat__chip wg-chat__chip--primary"
            disabled={!valid}
          >
            {it.submit_label || t('common.submit')}
          </button>
        </div>
      )}
    </form>
  );
};

export default FormRenderer;
